import { useIsAuthenticated } from "@azure/msal-react";
import {
  Table,
  TableCaption,
  Thead,
  Tr,
  Tbody,
  Td,
  Spinner,
} from "@chakra-ui/react";
import { useState, useCallback, useEffect, useMemo } from "react";
import { SituationModel, getSituationList } from "../../clients/BackOfficeApi";
import {
  usePagination,
  withPagination,
} from "../../contexts/PaginationContext";
import { SituationListRow } from "./SituationListRow";
import { SituationHeaderRow } from "./SituationHeaderRow";
import { PaginationForm } from "../../elements/PaginationForm";
import { TableLoadingRow } from "../../elements/TableLoadingRow";

interface Props {
  residenceTypeId?: number;
}
export const SituationTable = withPagination<Props>(
  { settingsKey: "situations" },
  ({ residenceTypeId }: Props) => {
    const [situations, setSituations] = useState<SituationModel[]>([]);
    const isAuthenticated = useIsAuthenticated();
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const { limit, offset } = usePagination();
    const [loadedOffset, setLoadedOffset] = useState(0);
    const [tableSpan, setTableSpan] = useState(1);

    const getSituations = useCallback(
      (refetch = false) => {
        if (!isAuthenticated) return;

        setLoading(true);

        const abortController = new AbortController();

        const query = refetch
          ? { Offset: 0, Limit: offset + limit, residenceTypeId }
          : { Offset: offset, Limit: limit, residenceTypeId };

        getSituationList({
          query,
          signal: abortController.signal,
          throwOnError: true,
        })
          .then(
            ({ data }) => {
              if (abortController.signal.aborted) return;

              const items = data?.items ?? [];

              const loadedItems = items.length;
              setLoadedOffset(query.Offset + loadedItems);
              setSituations((prev) => {
                prev.splice(query.Offset, loadedItems, ...items);
                return prev.slice(0, query.Offset + loadedItems);
              });
              setTotal(data?.totalCount ?? 0);
            },
            (e) => {
              if (abortController.signal.aborted) return;

              throw e;
            },
          )
          .finally(() => setLoading(false));

        return () =>
          abortController.abort("Component unmounted or re-rendered");
      },
      [isAuthenticated, limit, offset, residenceTypeId],
    );

    const moreRowsAvailable = useMemo(
      () => loadedOffset < total,
      [loadedOffset, total],
    );

    useEffect(() => getSituations(), [getSituations]);

    return (
      <Table variant="striped" w="auto">
        <TableCaption placement="top" whiteSpace="nowrap">
          Situaties ({total})
        </TableCaption>
        <Thead>
          <SituationHeaderRow
            ref={(el: HTMLTableRowElement | null) =>
              setTableSpan(el?.childElementCount ?? 1)
            }
            includeResidenceTypeDetails={!residenceTypeId}
          />
        </Thead>
        <Tbody>
          <TableLoadingRow colSpan={tableSpan} loading={!moreRowsAvailable && loading} />
          {!situations.length && !loading && (
            <Tr>
              <Td colSpan={tableSpan}>Geen situaties gevonden</Td>
            </Tr>
          )}
          {situations.map((situation) => (
            <SituationListRow
              key={situation.id}
              includeResidenceTypeDetails={!residenceTypeId}
              onChange={() => getSituations(true)}
              situation={situation}
            />
          ))}
          {moreRowsAvailable && (
            <Tr>
              <Td colSpan={tableSpan}>
                <PaginationForm loading={loading} offset={loadedOffset} />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    );
  },
);
